import React, { useEffect, useRef, useState } from "react";
import { api, fmtDateTime, errMsg } from "@/lib/api";
import { AudioWave } from "@/components/AudioWave";
import { Empty, SectionHead } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mic2, Fingerprint, Trash2, ShieldCheck, Loader2, CheckCircle2, FlaskConical, X, UserCheck, HelpCircle } from "lucide-react";

const TARGET = 20;
const TEST_TARGET = 10;   // seconds for voice test recording
const BARS = 44;

const pickMime = () => {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  for (const c of candidates) {
    if (window.MediaRecorder?.isTypeSupported?.(c)) return c;
  }
  return "";
};

export default function VoiceEnrollment() {
  const { user, setUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState("");
  const [prints, setPrints] = useState([]);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [levels, setLevels] = useState(() => new Array(BARS).fill(0.08));

  // voice test state
  const [testPhase, setTestPhase] = useState("idle"); // idle|recording|processing|done
  const [testSecs, setTestSecs] = useState(0);
  const [testResult, setTestResult] = useState(null);

  const timer = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("");
  const ctxRef = useRef(null);
  const rafRef = useRef(null);

  const testTimerRef = useRef(null);
  const testStreamRef = useRef(null);
  const testRecorderRef = useRef(null);
  const testChunksRef = useRef([]);
  const testMimeRef = useRef("");
  const testCtxRef = useRef(null);
  const testRafRef = useRef(null);
  const [testLevels, setTestLevels] = useState(() => new Array(BARS).fill(0.08));

  const load = async () => {
    try {
      const [u, v] = await Promise.all([api.get("/users"), api.get("/voiceprints")]);
      setUsers(u.data); setPrints(v.data);
      if (!target && user) setTarget(user.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { load(); return () => teardown(); /* eslint-disable-next-line */ }, []);

  const teardown = () => {
    clearInterval(timer.current);
    cancelAnimationFrame(rafRef.current);
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (e) { void e; }
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { ctxRef.current?.close(); } catch (e) { void e; }
    ctxRef.current = null;
    setLevels(new Array(BARS).fill(0.08));
  };

  const meter = (stream) => {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    ctxRef.current = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.75;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const loop = () => {
      analyser.getByteFrequencyData(buf);
      const step = Math.floor(buf.length / BARS) || 1;
      const next = new Array(BARS);
      for (let i = 0; i < BARS; i++) {
        let sum = 0;
        for (let j = 0; j < step; j++) sum += buf[i * step + j] || 0;
        next[i] = Math.max(0.08, Math.min(1, (sum / step / 255) * 2.4));
      }
      setLevels(next);
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();
  };

  const startRec = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Perekaman mikrofon tidak didukung browser ini.");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
    } catch (e) {
      toast.error("Akses mikrofon ditolak. Izinkan mikrofon pada browser lalu coba lagi.");
      return;
    }
    streamRef.current = stream;
    meter(stream);

    const mimeType = pickMime();
    mimeRef.current = mimeType;
    let rec;
    try {
      rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      toast.error("Perekaman audio tidak didukung browser ini.");
      teardown();
      return;
    }
    chunksRef.current = [];
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
    rec.onstop = () => { upload(secs); };
    rec.onerror = () => { toast.error("Perekaman terhenti oleh browser."); teardown(); };
    recorderRef.current = rec;
    rec.start();

    setRecording(true); setSecs(0);
    timer.current = setInterval(() => {
      setSecs((s) => {
        if (s + 1 >= TARGET) { stopRec(); return TARGET; }
        return s + 1;
      });
    }, 1000);
  };

  const stopRec = () => {
    clearInterval(timer.current);
    setRecording(false);
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    } catch (e) { void e; }
  };

  const upload = async (duration) => {
    setBusy(true);
    const durationSeconds = Math.max(duration, 4);
    try {
      const blob = new Blob(chunksRef.current, { type: mimeRef.current || "audio/webm" });
      if (blob.size < 4000) throw new Error("empty");
      const ext = mimeRef.current.includes("mp4") ? "m4a" : mimeRef.current.includes("ogg") ? "ogg" : "webm";
      const form = new FormData();
      form.append("file", blob, `enroll-${Date.now()}.${ext}`);
      const uid = target || user.id;
      const { data } = await api.post(
        `/voiceprints/enroll-audio?user_id=${encodeURIComponent(uid)}&duration_seconds=${durationSeconds}`,
        form
      );
      toast.success(`Voiceprint terdaftar · kualitas ${(data.quality * 100).toFixed(1)}%`);
      if (uid === user.id) setUser({ ...user, voice_enrolled: true });
      await load();
    } catch (e) {
      if (e.message === "empty") toast.error("Rekaman kosong. Coba rekam ulang dengan mikrofon aktif.");
      else toast.error(errMsg(e));
    } finally {
      setBusy(false); setSecs(0); chunksRef.current = [];
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      try { ctxRef.current?.close(); } catch (err) { void err; }
      ctxRef.current = null;
      cancelAnimationFrame(rafRef.current);
      setLevels(new Array(BARS).fill(0.08));
    }
  };

  const cancelRec = () => {
    clearInterval(timer.current);
    setRecording(false); setSecs(0);
    if (recorderRef.current) recorderRef.current.onstop = null;
    teardown();
    chunksRef.current = [];
  };

  /* ────────────────── voice test ────────────────── */
  const teardownTest = () => {
    clearInterval(testTimerRef.current);
    cancelAnimationFrame(testRafRef.current);
    try { if (testRecorderRef.current && testRecorderRef.current.state !== "inactive") testRecorderRef.current.stop(); } catch (e) { void e; }
    testRecorderRef.current = null;
    testStreamRef.current?.getTracks().forEach((t) => t.stop());
    testStreamRef.current = null;
    try { testCtxRef.current?.close(); } catch (e) { void e; }
    testCtxRef.current = null;
    setTestLevels(new Array(BARS).fill(0.08));
  };

  const meterTest = (stream) => {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    testCtxRef.current = ctx;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.75;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const loop = () => {
      analyser.getByteFrequencyData(buf);
      const step = Math.floor(buf.length / BARS) || 1;
      const next = new Array(BARS);
      for (let i = 0; i < BARS; i++) {
        let sum = 0;
        for (let j = 0; j < step; j++) sum += buf[i * step + j] || 0;
        next[i] = Math.max(0.08, Math.min(1, (sum / step / 255) * 2.4));
      }
      setTestLevels(next);
      testRafRef.current = requestAnimationFrame(loop);
    };
    loop();
  };

  const startTest = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Perekaman mikrofon tidak didukung browser ini.");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
    } catch (e) {
      toast.error("Akses mikrofon ditolak.");
      return;
    }
    testStreamRef.current = stream;
    meterTest(stream);

    const mimeType = pickMime();
    testMimeRef.current = mimeType;
    let rec;
    try {
      rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      toast.error("Perekaman tidak didukung browser ini.");
      teardownTest();
      return;
    }
    testChunksRef.current = [];
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) testChunksRef.current.push(ev.data); };
    rec.onstop = () => uploadTest(testSecs);
    rec.onerror = () => { toast.error("Perekaman terhenti."); teardownTest(); setTestPhase("idle"); };
    testRecorderRef.current = rec;
    rec.start();

    setTestPhase("recording"); setTestSecs(0); setTestResult(null);
    testTimerRef.current = setInterval(() => {
      setTestSecs((s) => {
        if (s + 1 >= TEST_TARGET) { stopTest(); return TEST_TARGET; }
        return s + 1;
      });
    }, 1000);
  };

  const stopTest = () => {
    clearInterval(testTimerRef.current);
    setTestPhase("processing");
    try {
      if (testRecorderRef.current && testRecorderRef.current.state !== "inactive") testRecorderRef.current.stop();
    } catch (e) { void e; }
  };

  const cancelTest = () => {
    clearInterval(testTimerRef.current);
    setTestPhase("idle"); setTestSecs(0); setTestResult(null);
    if (testRecorderRef.current) testRecorderRef.current.onstop = null;
    teardownTest();
    testChunksRef.current = [];
  };

  const uploadTest = async (duration) => {
    try {
      const blob = new Blob(testChunksRef.current, { type: testMimeRef.current || "audio/webm" });
      if (blob.size < 2000) {
        toast.error("Rekaman terlalu pendek. Coba bicara lebih dekat ke mikrofon.");
        setTestPhase("idle"); teardownTest(); return;
      }
      const ext = testMimeRef.current.includes("mp4") ? "m4a" : testMimeRef.current.includes("ogg") ? "ogg" : "webm";
      const form = new FormData();
      form.append("file", blob, `test-${Date.now()}.${ext}`);
      const { data } = await api.post("/voiceprints/test-voice", form);
      setTestResult(data);
      setTestPhase("done");
    } catch (e) {
      toast.error(errMsg(e));
      setTestPhase("idle");
    } finally {
      teardownTest(); testChunksRef.current = [];
    }
  };

  const remove = async (uid) => {
    try { await api.delete(`/voiceprints/${uid}`); toast.success("Voiceprint dihapus"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-7" data-testid="voice-enrollment-page">
      <div>
        <p className="label-xs">Voice Biometrics</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Voice Enrollment</h1>
        <p className="mt-2 text-sm text-slate-500">
          Rekam 20 detik suara asli Anda melalui mikrofon untuk menghasilkan voiceprint akustik nyata yang dipakai untuk mengenali pembicara pada rapat live.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
        <div className="clay overflow-hidden" data-testid="enroll-console">
          <div className="relative p-8 bg-brand-900">
            <div className="absolute inset-0 grad-brand opacity-35" />
            <div className="relative z-10 text-center">
              <div className="mx-auto relative grid place-items-center h-20 w-20 rounded-full bg-white/12 backdrop-blur">
                <Mic2 size={30} className="text-white" />
                {recording && <span className="absolute inset-0 rounded-full border-2 border-white/60 animate-pulse-ring" />}
              </div>
              <p className="mt-6 font-mono text-4xl font-semibold text-white tabular-nums" data-testid="enroll-timer">
                {String(secs).padStart(2, "0")} / {TARGET}s
              </p>
              <p className="mt-2 text-sm text-white/70">
                {recording ? "Bicara normal: “Saya bersedia mengikuti rapat ini dengan penuh tanggung jawab.”" : "Siap merekam sampel suara"}
              </p>
              <div className="mt-7"><AudioWave active={recording} bars={BARS} height={64} levels={recording ? levels : null} /></div>
              <div className="mt-7">
                <Progress value={(secs / TARGET) * 100} className="h-1.5 bg-white/20" />
              </div>
            </div>
          </div>
          <div className="p-6 space-y-5">
            <div className="space-y-2">
              <p className="label-xs">Daftarkan untuk</p>
              <Select value={target} onValueChange={setTarget} disabled={recording}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="enroll-target-select"><SelectValue placeholder="Pilih pengguna" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {u.role_label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              {!recording ? (
                <Button className="h-11 flex-1 rounded-xl grad-brand text-white" onClick={startRec} disabled={busy || !target} data-testid="start-enroll-btn">
                  {busy ? <><Loader2 size={16} className="mr-2 animate-spin" /> Memproses…</> : <><Fingerprint size={16} className="mr-2" /> Mulai Rekam 20 Detik</>}
                </Button>
              ) : (
                <>
                  <Button className="h-11 flex-1 rounded-xl grad-brand text-white" onClick={stopRec} disabled={secs < 5} data-testid="finish-enroll-btn">
                    <CheckCircle2 size={16} className="mr-2" /> Selesaikan ({secs}s)
                  </Button>
                  <Button variant="outline" className="h-11 rounded-xl border-slate-200" onClick={cancelRec} data-testid="cancel-enroll-btn">Batal</Button>
                </>
              )}
            </div>
            <div className="flex items-start gap-2.5 rounded-xl bg-slate-50 p-4">
              <ShieldCheck size={16} className="mt-0.5 shrink-0 text-emerald-600" />
              <p className="text-xs leading-relaxed text-slate-500">
                Voiceprint akustik disimpan terenkripsi dan dipakai untuk mencocokkan pembicara pada rapat live secara realtime.
              </p>
            </div>
          </div>
        </div>

        <div className="clay p-6" data-testid="voiceprint-list">
          <SectionHead title="Voiceprint Terdaftar" sub={`${prints.length} voiceprint aktif pada sistem`} />
          {prints.length === 0 ? (
            <Empty icon={Fingerprint} title="Belum ada voiceprint" sub="Daftarkan suara peserta agar sistem dapat mengenali pembicara otomatis." />
          ) : (
            <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
              {prints.map((p) => (
                <div key={p.id} className="flex items-center gap-3.5 rounded-xl border border-slate-200/70 p-4" data-testid={`voiceprint-${p.user_id}`}>
                  <span className="grid place-items-center h-10 w-10 shrink-0 rounded-full grad-brand text-white text-[11px] font-semibold">
                    {(p.user_name || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800">{p.user_name}</p>
                    <p className="mt-0.5 truncate font-mono text-[11px] text-slate-400">{p.hash} · {p.dimensions}d</p>
                    <p className="text-xs text-slate-500">{fmtDateTime(p.created_at)} · {p.duration_seconds}s</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono text-sm font-semibold text-emerald-600">{(p.quality * 100).toFixed(1)}%</p>
                    <p className="text-[10px] text-slate-400">kualitas</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => remove(p.user_id)} data-testid={`delete-voiceprint-${p.user_id}`}>
                    <Trash2 size={15} className="text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ──────────── Voice Test Panel ──────────── */}
      <div className="clay overflow-hidden" data-testid="voice-test-panel">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-violet-100 text-violet-600">
              <FlaskConical size={17} />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Uji Akurasi Suara</h2>
              <p className="text-xs text-slate-500">Rekam 10 detik untuk melihat seberapa akurat sistem mengenali suara Anda</p>
            </div>
          </div>
          {testPhase === "done" && (
            <Button variant="ghost" size="sm" className="rounded-lg text-slate-400 hover:text-slate-600" onClick={() => { setTestPhase("idle"); setTestResult(null); }} data-testid="reset-test-btn">
              <X size={15} className="mr-1.5" /> Ulangi
            </Button>
          )}
        </div>

        <div className="p-6">
          {testPhase === "idle" && (
            <div className="flex flex-col items-center gap-5 py-4">
              {prints.length === 0 ? (
                <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4 w-full max-w-lg">
                  <HelpCircle size={16} className="mt-0.5 shrink-0 text-amber-600" />
                  <p className="text-sm text-amber-700">Daftarkan minimal satu voiceprint terlebih dahulu sebelum melakukan uji suara.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500 text-center max-w-md">
                    Tekan tombol di bawah, bicara normal selama ~10 detik, lalu sistem akan mencocokkan suara Anda dengan voiceprint yang terdaftar.
                  </p>
                  <Button className="h-11 px-8 rounded-xl grad-brand text-white" onClick={startTest} data-testid="start-test-btn">
                    <FlaskConical size={16} className="mr-2" /> Mulai Uji Suara
                  </Button>
                </>
              )}
            </div>
          )}

          {testPhase === "recording" && (
            <div className="space-y-5">
              <div className="relative rounded-2xl bg-brand-900 p-6 text-center overflow-hidden">
                <div className="absolute inset-0 grad-brand opacity-30" />
                <div className="relative z-10">
                  <div className="mx-auto relative grid place-items-center h-14 w-14 rounded-full bg-white/15 backdrop-blur mb-4">
                    <Mic2 size={22} className="text-white" />
                    <span className="absolute inset-0 rounded-full border-2 border-white/60 animate-pulse-ring" />
                  </div>
                  <p className="font-mono text-3xl font-bold text-white tabular-nums" data-testid="test-timer">
                    {String(testSecs).padStart(2, "0")} / {TEST_TARGET}s
                  </p>
                  <p className="mt-1.5 text-xs text-white/70">Bicara normal…</p>
                  <div className="mt-4"><AudioWave active bars={BARS} height={48} levels={testLevels} /></div>
                  <div className="mt-3"><Progress value={(testSecs / TEST_TARGET) * 100} className="h-1 bg-white/20" /></div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button className="h-10 flex-1 rounded-xl grad-brand text-white text-sm" onClick={stopTest} disabled={testSecs < 3} data-testid="stop-test-btn">
                  <CheckCircle2 size={15} className="mr-1.5" /> Selesai ({testSecs}s)
                </Button>
                <Button variant="outline" className="h-10 rounded-xl border-slate-200 text-sm" onClick={cancelTest} data-testid="cancel-test-btn">Batal</Button>
              </div>
            </div>
          )}

          {testPhase === "processing" && (
            <div className="flex flex-col items-center gap-4 py-8" data-testid="test-processing">
              <Loader2 size={32} className="animate-spin text-brand-600" />
              <p className="text-sm font-medium text-slate-600">Menganalisis voiceprint…</p>
              <p className="text-xs text-slate-400">Membandingkan dengan {prints.length} voiceprint terdaftar</p>
            </div>
          )}

          {testPhase === "done" && testResult && (
            <div className="space-y-5" data-testid="test-result">
              {/* Top match banner */}
              <div className={`flex items-center gap-4 rounded-xl p-4 ${testResult.is_unknown ? "bg-amber-50 border border-amber-200" : "bg-emerald-50 border border-emerald-200"}`}>
                <span className={`grid place-items-center h-11 w-11 shrink-0 rounded-full text-white text-sm font-bold ${testResult.is_unknown ? "bg-amber-400" : "grad-brand"}`}>
                  {testResult.is_unknown ? <HelpCircle size={18} /> : <UserCheck size={18} />}
                </span>
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${testResult.is_unknown ? "text-amber-800" : "text-emerald-800"}`}>
                    {testResult.is_unknown ? "Suara tidak dikenali" : `Teridentifikasi: ${testResult.identified_as}`}
                  </p>
                  <p className={`text-xs mt-0.5 ${testResult.is_unknown ? "text-amber-600" : "text-emerald-600"}`}>
                    Kepercayaan: <span className="font-mono font-semibold">{((testResult.confidence || 0) * 100).toFixed(1)}%</span>
                  </p>
                </div>
                <p className={`font-mono text-2xl font-bold ${testResult.is_unknown ? "text-amber-500" : "text-emerald-600"}`} data-testid="test-confidence-pct">
                  {((testResult.confidence || 0) * 100).toFixed(0)}%
                </p>
              </div>

              {/* Per-user similarity bars */}
              {testResult.matches && testResult.matches.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Skor kemiripan per peserta</p>
                  {testResult.matches.map((m) => (
                    <div key={m.user_id} className="space-y-1.5" data-testid={`test-match-${m.user_id}`}>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          {m.is_top_match && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />}
                          {m.user_name}
                          {m.is_top_match && <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Cocok</span>}
                        </span>
                        <span className="font-mono text-xs font-semibold text-slate-600">{(m.similarity * 100).toFixed(1)}%</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${m.is_top_match ? "bg-emerald-500" : "bg-slate-400"}`}
                          style={{ width: `${Math.min(100, m.similarity * 100).toFixed(1)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {testResult.is_unknown && (
                <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 p-3.5">
                  <HelpCircle size={14} className="mt-0.5 shrink-0 text-amber-600" />
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Suara tidak cocok dengan voiceprint yang terdaftar. Coba rekam ulang enrollment dengan suara yang lebih keras dan jelas, atau tambahkan voiceprint baru untuk pengguna ini.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
